import { Cube01 } from "@untitledui/icons";
import { DisplayInput } from "@/components/Atoms/DisplayField";
import { RequiredAttachmentsField } from "@/features/attachment-requirements/required-attachments-field";
import { EntityTypeDetailsPanel } from "@/features/entity-types/components/entity-type-details-panel";
import { RequiredLocationField } from "@/features/entity-types/components/required-location-field";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { formatGeometryTypeLabel } from "@/utils/asset-type/asset-geometry-types.utils";
import { resolveIconComponent } from "@/utils/icon-catalog";
import { useAssetTypeDrawerContext } from "./asset-type-drawer-context";

/** Empty or unknown drafts use the view glyph (stored icon, type name, then Cube01). */
export function resolveAssetTypeEditIconName(
	draftIcon: string,
	assetType: { icon?: string | null; name?: string | null },
): string {
	if (resolveIconComponent(draftIcon)) return draftIcon;
	const viewName = assetType.icon || assetType.name || "";
	return resolveIconComponent(viewName) ? viewName : "Cube01";
}

export function AssetTypeDetailsSection() {
	const { t } = useAppTranslation();
	const {
		assetType,
		canEdit,
		persistDetails,
		locationRequired,
		persistLocationRequired,
	} = useAssetTypeDrawerContext();

	const sourceLabel = assetType.defaultType
		? t("admin.assetTypes.table.source.system")
		: t("admin.assetTypes.table.source.custom");

	return (
		<EntityTypeDetailsPanel
			labels={{
				name: t("admin.assetTypes.drawer.details.fields.name"),
				icon: t("admin.assetTypes.drawer.details.fields.icon"),
				source: t("admin.assetTypes.drawer.details.fields.source"),
				lifecycle: t("admin.assetTypes.drawer.details.fields.lifecycle"),
				id: t("admin.assetTypes.drawer.details.fields.id"),
				description: t("admin.assetTypes.drawer.details.fields.description"),
				nameRequired: t("admin.entityTypes.drawer.details.nameRequired"),
				saveFailed: t("admin.entityTypes.drawer.details.saveFailed"),
			}}
			name={assetType.name}
			icon={assetType.icon}
			description={assetType.description}
			sourceLabel={sourceLabel}
			lifecycleLabel={assetType.statusLifecycle?.name?.trim()}
			showLifecycle
			id={assetType.id}
			resolveIcon={(icon) =>
				resolveIconComponent(icon || assetType.name || "") ?? Cube01
			}
			iconEditable
			extraRows={
				<DisplayInput
					label={t("admin.assetTypes.table.columns.geometryType")}
					value={
						assetType.geometryType
							? formatGeometryTypeLabel(assetType.geometryType)
							: undefined
					}
					showEdit={false}
					data-testid="entity-type-geometry-field"
				/>
			}
			locationRequiredField={
				<RequiredLocationField
					value={locationRequired}
					canEdit={canEdit}
					onChange={persistLocationRequired}
				/>
			}
			attachmentsField={
				<RequiredAttachmentsField
					entityKind="asset"
					typeId={assetType.id}
					requirement={assetType.attachmentRequirement}
				/>
			}
			canEdit={canEdit}
			onPersist={persistDetails}
			data-testid="assettype-details-panel"
		/>
	);
}
